import React, { useContext, useState, useEffect, useMemo } from "react";
import Checkbox from "../../ui/Checkox/Checkbox.js";
import Select from "../../ui/Select/Select";
import PlusControl from "../../ui/PlusControl/PlusControl";
import Button from "../../ui/Button/Button";
import SortGroup from "../../ui/SortGroup/SortGroup";
import ActionGroup from "../../ui/ActionGroup/ActionGroup";
import ClosableLabel from "../../ui/ClosableLabel/ClosableLabel";
import FeedMainStat from "../../ui/FeedMainStat/FeedMainStat";
import GlobalsContext from "../../context/GlobalsContext";

import IfPermission from "../../components/IfPermission";
import { allowGroupActions } from "../../utils/perm";

import CategoriesSelect from "../../components/CategoriesSelect";
import { canViewDeleted } from "../../utils/perm";
import { authorGroups, teaserStatus } from "../../constants";
import useCampaignsSelect from "../../hooks/useCampaignsSelect";

import * as perm from "../../utils/perm";

const statusOptions = [
  { label: "Возможные к показу", value: teaserStatus.canShow },
  { label: "Активные сейчас", value: teaserStatus.activeNow },
  { label: "Активные", value: teaserStatus.active },
  { label: "Остановленные", value: teaserStatus.inactive },
  { label: "Отклоненные", value: teaserStatus.reject },
  { label: "Модерация", value: teaserStatus.moderate }
];

const deleteStatusOption = { label: "Удаленные", value: teaserStatus.delete };

function getStatusOptions(permissions) {
  if (canViewDeleted(permissions)) {
    return [...statusOptions, deleteStatusOption];
  }
  return statusOptions;
}

const getFilters = (user, reqUser, geos, search, mpaaRatings) => {
  const filterMap = {
    area: {
      0: "Везде",
      1: "В топе",
      2: "В инф"
    },
    device: {
      mobile: "Мобильные",
      pc: "ПК",
      tablet: "Планшеты"
    },
    payout_status: {
      1: "OK",
      2: "Отказ",
      3: "Штраф",
      4: "Ошибка",
      5: "В процессе"
    },
    grade_status: {
      1: "Оценка",
      2: "Оплачен",
      3: "Не оплачен"
    },
    mpaa_rating: mpaaRatings
  };

  filterMap.user = {
    [reqUser.id]: reqUser.name,
    [authorGroups.editors]: "Редакторы",
    [authorGroups.owners]: "Владельцы"
  };

  if (user) {
    const parsedUser = Array.isArray(user) ? user : [user];

    filterMap.user = parsedUser.reduce((previous, current) => {
      previous[current.value.toString()] = current.name;
      return previous;
    }, {});
  }

  filterMap.geo = geos.reduce((prev, current) => {
    prev[current.value.toString()] = current.name;
    return prev;
  }, {});

  const filters = {};

  Object.keys(search).forEach(key => {
    const values = filterMap[key] || {};

    if (key === "user") {
      if (!user) {
        filters["user"] = {
          [reqUser.id]: reqUser.name,
          [authorGroups.editors]: "Редакторы",
          [authorGroups.owners]: "Владельцы"
        };

        return;
      }

      const parsedValue = (() => {
        const value = search["user"];

        if (value === "" || value === null || value === undefined) {
          return [];
        }

        if (typeof value === "string") {
          return value.split(",").map(Number);
        }

        return Array.isArray(value) ? value : [value];
      })();

      filters["user"] = parsedValue.reduce((previous, current) => {
        previous[current.toString()] = values[current];

        return previous;
      }, {});
    } else {
      if (!!values[search[key]]) {
        filters[key] = { [key]: values[search[key]] };
      }
    }
  });

  return filters;
};

const TeaserListControls = ({
  globalCheck,
  setGa,
  search,
  setSearch,
  checked,
  onStopClick,
  onStartClick,
  onCopyClick,
  onDeleteClick,
  onShowUrlChange,
  showUrl,
  userCardsLength,
  totalCount,
  totalStat,
  handleCampaignSelect,
  showDisclaimer,
  onShowDisclaimerChange
}) => {
  const globalsContext = useContext(GlobalsContext);

  const [user, setUser] = useState(globalsContext.author_filter);
  const campaignsSelect = useCampaignsSelect(
    parseInt(search.cid),
    userCardsLength
  );

  useEffect(() => {
    if (!perm.hasAnyCampaignAccess(globalsContext.permissions)) {
      if (userCardsLength) {
        campaignsSelect.setDisabled(false);
      } else {
        campaignsSelect.setDisabled(true);
      }
    }
  }, [userCardsLength]);

  const getSortBy = () => {
    return search.sort_by || "date_add";
  };

  const getSortOrder = () => {
    return search.sort_order || "desc";
  };

  const handleSortChange = ({ sortBy, sortOrder }) => {
    setSearch({ sort_by: sortBy, sort_order: sortOrder });
  };

  const getShowOt = () => {
    return search.show_ot == "true";
  };

  const getShowUrl = () => {
    return showUrl;
  };

  const getStatus = () => {
    const s = search.status || teaserStatus.canShow;
    return parseInt(s);
  };

  const onCtShowChange = e => {
    setSearch({ show_ot: e.target.checked });
  };

  const onUserSelect = ({ user }) => {
    setUser(user);
  };

  const resetFilters = () => {
    setSearch({
      status: teaserStatus.canShow,
      category: "",
      area: "",
      device: "",
      campaign: "",
      user: undefined,
      geo: "",
      payout_status: "",
      grade_status: ""
    });
  };

  const mpaaRatings = useMemo(() => {
    const result = {
      0: "Без рейтинга"
    };

    globalsContext.mpaa_rating.forEach(item => {
      result[item.id] = item.name;
    });

    return result;
  }, [globalsContext.mpaa_rating]);

  const filters = getFilters(
    user,
    globalsContext.user,
    globalsContext.geos,
    search,
    mpaaRatings
  );

  const hasChecked = globalCheck || checked.length != 0;
  const checkedCnt = globalCheck ? "все" : checked.length;

  const handleSearch = (name, values, deletedValue) => {
    const clonedValues = { ...values };

    delete clonedValues[deletedValue];

    const newValue = Object.keys(clonedValues).join(",");

    setSearch({ [name]: newValue || undefined });
  };

  const hasDisclaimer =
    search.has_disclaimer === "true" || search.has_disclaimer === true;

  const onHasDisclaimerChange = e => {
    setSearch({ has_disclaimer: e.target.checked });
  };

  return (
    <div className="teaser-list-controls">
      <div className="content-body">
        <div className="teaser-list-controls__top">
          <Checkbox
            id="x1"
            checked={globalCheck}
            onChange={e => {
              setGa(e.target.checked);
            }}
          />
          <Select
            placeholder="Выбор кампании"
            className="campaign"
            value={parseInt(search.cid)}
            onSelect={handleCampaignSelect}
            {...campaignsSelect}
          />
          <CategoriesSelect
            placeholder="Выбор категории"
            className="category"
            value={search.category}
            withGroups={true}
            onSelect={item => {
              setSearch({ category: item.value });
            }}
          />
          <Select
            options={getStatusOptions(globalsContext.permissions)}
            onSelect={item => setSearch({ status: item.value })}
            value={getStatus()}
            className="status"
          />
          <PlusControl
            onFilterChange={f => {
              setSearch(f);
            }}
            showOt={getShowOt()}
            showUrl={getShowUrl()}
            hasDisclaimer={hasDisclaimer}
            showDisclaimer={showDisclaimer}
            onDisclaimerShowChange={onShowDisclaimerChange}
            onCtShowChange={onCtShowChange}
            onUrlShowChange={onShowUrlChange}
            onHasDisclaimerChange={onHasDisclaimerChange}
            selectedFilters={search}
            onUserSelect={onUserSelect}
            permissions={globalsContext.permissions}
            mpaaRatings={globalsContext.mpaa_rating}
          />
          <Button
            color="light-blue teaser-list-controls__reset"
            onClick={resetFilters}
          >
            Сброс
          </Button>
          <SortGroup
            sortBy={getSortBy()}
            sortOrder={getSortOrder()}
            onChange={handleSortChange}
            permissions={globalsContext.permissions}
          />
        </div>
        <div className="teaser-list-controls__bottom">
          {hasChecked ? (
            <IfPermission checker={allowGroupActions}>
              <ActionGroup
                cnt={checkedCnt}
                onCopyClick={onCopyClick}
                onStopClick={onStopClick}
                onStartClick={onStartClick}
                onDeleteClick={onDeleteClick}
              />
            </IfPermission>
          ) : (
            <React.Fragment>
              {Object.entries(filters).map(([name, values]) => {
                return Object.entries(values).map(([id, value]) => {
                  return (
                    <ClosableLabel
                      key={id}
                      onClose={() => {
                        handleSearch(name, values, id);
                      }}
                    >
                      {value}
                    </ClosableLabel>
                  );
                });
              })}
            </React.Fragment>
          )}

          <FeedMainStat stat={totalStat} count={totalCount} />
        </div>
      </div>
    </div>
  );
};

export default TeaserListControls;
